import type { Connection } from '@xyflow/react'

import { resolveCanvasScope, type CanvasScopeType } from '@/features/studio/lib/canvas-scope'
import { isValidConnectionTypes, isValidStudioConnection } from './handles'
import { mergeGenParams } from './model-params'
import { estimateNodeSize } from './node-bounds'
import { createInitialAudioNodeState, ensureAudioNodeData } from './audio-tabs'
import { resolveMediaDisplayUrl } from '@/features/studio/lib/media-display'
import {
  LEGACY_NODE_MAP,
  GEN_NODE_TYPES,
  type CanvasCtx,
  type HandleDataType,
  type StudioFlowEdge,
  type StudioFlowNode,
  type StudioNodeData,
  type StudioNodeType,
  type UpstreamPreview,
} from './types'

let nodeSeq = 0

export function nextNodeId(prefix = 'n'): string {
  nodeSeq += 1
  return `${prefix}_${Date.now()}_${nodeSeq}`
}

/** 旧节点类型 → 新节点类型迁移 */
export function migrateNodeType(type: string): StudioNodeType {
  if (type in LEGACY_NODE_MAP) return LEGACY_NODE_MAP[type]
  return type as StudioNodeType
}

/** 加载已保存图时迁移节点：旧 gen_image/gen_video/character_lock → 新类型 */
export function migrateNode(node: StudioFlowNode): StudioFlowNode {
  const rawType = String(node.data.nodeType)
  const nextType = migrateNodeType(rawType)
  let nextNode: StudioFlowNode = node
  if (nextType !== (node.data.nodeType as StudioNodeType)) {
    const legacy = node.data as unknown as {
      characterLock?: { hint?: string; assetUrl?: string }
    }
    const nextData: StudioNodeData = { ...node.data, nodeType: nextType }
    if (rawType === 'character_lock' && legacy.characterLock) {
      nextData.refCharacter = {
        urls: legacy.characterLock.assetUrl ? [legacy.characterLock.assetUrl] : [],
        hint: legacy.characterLock.hint,
        name: node.data.label,
      }
    }
    nextNode = { ...node, data: nextData }
  }
  if (nextNode.data.nodeType === 'group' && nextNode.type !== 'group') {
    nextNode = {
      ...nextNode,
      type: 'group',
      zIndex: nextNode.zIndex ?? 0,
      style: nextNode.style ?? { width: 400, height: 280 },
    }
  }
  if (nextNode.parentId && nextNode.extent === 'parent') {
    nextNode = { ...nextNode, extent: undefined }
  }
  if (nextNode.data.nodeType === 'gen_audio') {
    nextNode = {
      ...nextNode,
      data: ensureAudioNodeData(nextNode.data, []),
    }
  }
  return nextNode
}

export function createStudioNode(
  nodeType: StudioNodeType,
  position: { x: number; y: number },
  partial?: Partial<StudioNodeData>,
): StudioFlowNode {
  const id = nextNodeId(nodeType.slice(0, 2))
  const base: StudioNodeData = {
    nodeType,
    label: partial?.label ?? nodeType,
    output: { status: 'idle' },
    ...partial,
  }

  // 生成类节点：自带 prompt + gen 默认值
  if (GEN_NODE_TYPES.includes(nodeType)) {
    base.prompt = partial?.prompt ?? { positive: '', negative: '' }
    if (nodeType === 'gen_audio') {
      base.audio = partial?.audio ?? createInitialAudioNodeState([])
      const active = base.audio.tabs[base.audio.activeTab]
      base.gen = partial?.gen ?? { modelId: active.modelId, params: active.params }
      base.takes = active.takes ?? []
    } else {
      base.gen = {
        modelId: partial?.gen?.modelId ?? '',
        params: mergeGenParams(nodeType, partial?.gen?.params),
      }
      base.takes = partial?.takes ?? []
    }
  }

  if (nodeType === 'prompt') {
    base.prompt = partial?.prompt ?? { positive: '', negative: '' }
  }
  if (nodeType === 'text') {
    base.text = partial?.text ?? { content: '', instruction: '' }
    base.gen = {
      modelId: partial?.gen?.modelId ?? '',
      params: partial?.gen?.params ?? {},
    }
  }
  if (nodeType === 'upload') {
    base.upload = partial?.upload ?? {}
  }
  if (nodeType === 'ref_character') {
    base.refCharacter = partial?.refCharacter ?? { urls: [], name: '', hint: '' }
  }
  if (nodeType === 'ref_scene') {
    base.refScene = partial?.refScene ?? { urls: [], name: '', hint: '' }
  }
  if (nodeType === 'register') {
    base.register = partial?.register ?? { kind: 'misc', name: '' }
  }
  if (nodeType === 'export') {
    base.export = partial?.export ?? { status: 'idle' }
  }
  if (nodeType === 'group') {
    base.groupMeta = partial?.groupMeta ?? { title: partial?.label ?? '分组', color: '#6366f1' }
    return {
      id,
      type: 'group',
      position,
      data: base,
      style: { width: 400, height: 280, ...(partial as { style?: Record<string, number> } | undefined)?.style },
      zIndex: 0,
    }
  }

  return {
    id,
    type: 'studio',
    position,
    data: base,
  }
}

/** 取节点当前输出的数据类型 */
export function nodeOutputDataType(node: StudioFlowNode): HandleDataType | undefined {
  return node.data.output?.dataType
}

/** 取节点输出 URL（图片/视频/音频统一字段） */
export function nodeOutputUrl(node: StudioFlowNode): string | undefined {
  return node.data.output?.previewUrl
}

function upstreamPreviewUrl(raw?: string): string | undefined {
  const resolved = resolveMediaDisplayUrl(raw)
  return resolved || undefined
}

export function computeUpstreamPreviews(
  nodeId: string,
  nodes: StudioFlowNode[],
  edges: StudioFlowEdge[],
): UpstreamPreview[] {
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const incoming = edges.filter((e) => e.target === nodeId)
  const previews: UpstreamPreview[] = []
  for (const edge of incoming) {
    const src = byId.get(edge.source)
    if (!src) continue
    // 优先用运行产出
    if (src.data.output?.previewUrl) {
      previews.push({
        nodeId: src.id,
        label: src.data.label,
        url: upstreamPreviewUrl(src.data.output.previewUrl),
        dataType: src.data.output.dataType ?? inferDataType(src),
      })
      continue
    }
    // 其次 ref / upload 节点自带的素材 URL
    const refUrl = firstRefUrl(src)
    if (refUrl) {
      previews.push({
        nodeId: src.id,
        label: src.data.label,
        url: upstreamPreviewUrl(refUrl),
        dataType: src.data.output?.dataType ?? inferDataType(src),
      })
      continue
    }
    // 仅占位
    previews.push({ nodeId: src.id, label: src.data.label, dataType: inferDataType(src) })
    if (previews.length >= 6) break
  }
  return previews
}

function firstRefUrl(node: StudioFlowNode): string | undefined {
  if (node.data.nodeType === 'ref_character') return node.data.refCharacter?.urls?.[0]
  if (node.data.nodeType === 'ref_scene') return node.data.refScene?.urls?.[0]
  if (node.data.nodeType === 'upload') return node.data.upload?.url
  return undefined
}

function inferDataType(node: StudioFlowNode): HandleDataType {
  switch (node.data.nodeType) {
    case 'gen_image':
    case 'ref_character':
    case 'ref_scene':
      return 'image'
    case 'upload': {
      const mime = (node.data.upload?.mime ?? '').toLowerCase()
      if (mime.startsWith('video/') || node.data.output?.dataType === 'video') return 'video'
      if (mime.startsWith('audio/') || node.data.output?.dataType === 'audio') return 'audio'
      return 'image'
    }
    case 'gen_video':
    case 'export':
      return 'video'
    case 'gen_audio':
      return 'audio'
    case 'prompt':
    case 'text':
      return 'text'
    case 'context':
      return 'ctx'
    default:
      return 'text'
  }
}

export function refreshUpstreamPreviews(nodes: StudioFlowNode[], edges: StudioFlowEdge[]): StudioFlowNode[] {
  return nodes.map((n) => ({
    ...n,
    data: {
      ...n.data,
      upstreamPreviews: computeUpstreamPreviews(n.id, nodes, edges),
    },
  }))
}

/** 收集上游 prompt / context / ref 的文本贡献，作为生成节点 prompt 的兜底拼接 */
export function collectPromptFromUpstream(
  nodeId: string,
  nodes: StudioFlowNode[],
  edges: StudioFlowEdge[],
): { positive: string; negative?: string } {
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const parts: string[] = []
  let negative = ''
  const visited = new Set<string>()

  function walk(id: string) {
    if (visited.has(id)) return
    visited.add(id)
    const incoming = edges.filter((e) => e.target === id)
    for (const edge of incoming) {
      walk(edge.source)
      const src = byId.get(edge.source)
      if (!src) continue
      // 独立 prompt 节点的文本
      if (src.data.nodeType === 'prompt' && src.data.prompt?.positive.trim()) {
        parts.push(src.data.prompt.positive.trim())
      }
      // 通用文本节点
      if (src.data.nodeType === 'text' && src.data.text?.content.trim()) {
        parts.push(src.data.text.content.trim())
      }
      // 上下文节点：优先完整 prompt
      if (src.data.nodeType === 'context') {
        const prompt = src.data.context?.prompt?.trim()
        if (prompt) parts.push(prompt)
        else if (src.data.context?.title) parts.push(src.data.context.title)
      }
      // 角色参考 hint
      if (src.data.nodeType === 'ref_character' && src.data.refCharacter?.hint?.trim()) {
        parts.push(`character: ${src.data.refCharacter.hint.trim()}`)
      }
      // 场景参考 hint
      if (src.data.nodeType === 'ref_scene' && src.data.refScene?.hint?.trim()) {
        parts.push(`scene: ${src.data.refScene.hint.trim()}`)
      }
      // 任意上游的负向 prompt（仅取第一个非空）
      if (!negative && src.data.prompt?.negative?.trim()) {
        negative = src.data.prompt.negative.trim()
      }
    }
  }

  walk(nodeId)
  return { positive: parts.join('\n'), negative: negative || undefined }
}

/** 收集上游图片 URL（用于图生图 / 图生视频的 reference_image payload） */
export function collectUpstreamImages(
  nodeId: string,
  nodes: StudioFlowNode[],
  edges: StudioFlowEdge[],
): string[] {
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const urls: string[] = []
  const visited = new Set<string>()

  function walk(id: string) {
    if (visited.has(id)) return
    visited.add(id)
    const incoming = edges.filter((e) => e.target === id)
    for (const edge of incoming) {
      // 仅直接上游的图（不递归穿透生成链，避免把更上游的旧产出当参考图）
      const src = byId.get(edge.source)
      if (!src) continue
      const fromGen = src.data.output?.previewUrl
      if (fromGen && src.data.output?.dataType === 'image') urls.push(fromGen)
      const fromRef = firstRefUrl(src)
      if (fromRef) urls.push(fromRef)
      if (src.data.nodeType === 'ref_character') {
        for (const u of src.data.refCharacter?.urls ?? []) urls.push(u)
      }
      if (src.data.nodeType === 'ref_scene') {
        for (const u of src.data.refScene?.urls ?? []) urls.push(u)
      }
    }
  }

  walk(nodeId)
  // 去重
  return Array.from(new Set(urls))
}

/** 收集上游音频 URL（用于 gen_audio 的音色参考 / export 合成） */
export function collectUpstreamAudio(
  nodeId: string,
  nodes: StudioFlowNode[],
  edges: StudioFlowEdge[],
): string[] {
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const urls: string[] = []
  for (const edge of edges.filter((e) => e.target === nodeId)) {
    const src = byId.get(edge.source)
    if (!src) continue
    if (src.data.output?.previewUrl && src.data.output.dataType === 'audio') {
      urls.push(src.data.output.previewUrl)
    }
    if (src.data.nodeType === 'upload' && src.data.upload?.mime?.startsWith('audio/')) {
      if (src.data.upload?.url) urls.push(src.data.upload.url)
    }
  }
  return Array.from(new Set(urls))
}

/** 收集上游视频 URL（用于 export 音画合成） */
export function collectUpstreamVideo(
  nodeId: string,
  nodes: StudioFlowNode[],
  edges: StudioFlowEdge[],
): string[] {
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const urls: string[] = []
  for (const edge of edges.filter((e) => e.target === nodeId)) {
    const src = byId.get(edge.source)
    if (!src) continue
    if (src.data.output?.previewUrl && (src.data.output.dataType === 'video' || inferDataType(src) === 'video')) {
      urls.push(src.data.output.previewUrl)
    }
    if (src.data.nodeType === 'upload' && inferDataType(src) === 'video' && src.data.upload?.url) {
      urls.push(src.data.upload.url)
    }
    if (src.data.nodeType === 'export' && src.data.export?.outputUrl) {
      urls.push(src.data.export.outputUrl)
    }
  }
  return Array.from(new Set(urls))
}

export { isValidStudioConnection }

/** 连接合法性校验：source/target handle 数据类型兼容（ctx 可接 text） */
export function isValidConnection(connection: Connection): boolean {
  return isValidConnectionTypes(connection)
}

export function placeBeside(source: StudioFlowNode, offsetX = 320, offsetY = 0): { x: number; y: number } {
  return { x: source.position.x + offsetX, y: source.position.y + offsetY }
}

import type { ShotCanvasIntent } from '@/features/studio/lib/shot-canvas-prompt'

export interface CanvasLaunchContext {
  ctx: CanvasCtx
  label: string
  entityId?: string
  shotId?: string
  shotIds?: string[]
  intent?: ShotCanvasIntent
  workItemId?: string
  scriptId?: number
  episodeNo?: number
  sceneNo?: number
  scopeType: CanvasScopeType
  scopeKey: string
  returnPath: string
}

function parseOptionalInt(raw: string | null): number | undefined {
  if (!raw) return undefined
  const n = Number(raw)
  return Number.isFinite(n) ? n : undefined
}

export function parseCanvasLaunch(search: URLSearchParams, fallbackReturn: string): CanvasLaunchContext {
  const ctx = (search.get('ctx') as CanvasCtx | null) ?? 'free'
  const entityId =
    search.get('entityId') ?? search.get('characterId') ?? search.get('locationId') ?? undefined
  const scriptId = parseOptionalInt(search.get('scriptId'))
  const episodeNo = parseOptionalInt(search.get('episodeNo'))
  const sceneNo = parseOptionalInt(search.get('sceneNo'))
  const scope = resolveCanvasScope({
    ctx: ctx || 'free',
    scopeType: search.get('scope_type') ?? undefined,
    scopeKey: search.get('scope_key') ?? undefined,
    entityId,
    shotId: search.get('shotId') ?? undefined,
    scriptId,
    episodeNo,
    sceneNo,
  })
  const shotIdsRaw = search.get('shotIds')?.trim()
  const shotIds = shotIdsRaw
    ? shotIdsRaw.split(',').map((id) => id.trim()).filter(Boolean)
    : undefined

  const intentRaw = search.get('intent')
  const intent: ShotCanvasIntent | undefined =
    intentRaw === 'video' || intentRaw === 'storyboard' ? intentRaw : undefined

  return {
    ctx: ctx || 'free',
    label: search.get('label') ?? search.get('title') ?? '',
    entityId,
    shotId: search.get('shotId') ?? undefined,
    shotIds,
    intent,
    workItemId: search.get('workItemId') ?? undefined,
    scriptId,
    episodeNo,
    sceneNo,
    scopeType: scope.scopeType,
    scopeKey: scope.scopeKey,
    returnPath: search.get('return') ?? fallbackReturn,
  }
}

/** 按入口上下文构建初始模板 */
export function buildCtxTemplate(ctx: CanvasLaunchContext): { nodes: StudioFlowNode[]; edges: StudioFlowEdge[] } {
  const nodes: StudioFlowNode[] = []
  const edges: StudioFlowEdge[] = []

  const ctxNode = createStudioNode('context', { x: 80, y: 160 }, {
    label: ctx.label || ctx.ctx,
    context: {
      ctx: ctx.ctx,
      entityId: ctx.entityId,
      title: ctx.label,
    },
  })
  nodes.push(ctxNode)

  // 角色/场景入口：加 ref 节点
  if (ctx.ctx === 'bible-character') {
    const refNode = createStudioNode('ref_character', { x: 80, y: 360 }, {
      label: ctx.label,
      refCharacter: { urls: [], name: ctx.label, hint: '' },
    })
    nodes.push(refNode)
  }
  if (ctx.ctx === 'bible-location' || ctx.ctx === 'bible-prop' || ctx.ctx === 'bible-costume' || ctx.ctx === 'bible-style') {
    const refNode = createStudioNode('ref_scene', { x: 80, y: 360 }, {
      label: ctx.label,
      refScene: { urls: [], name: ctx.label, hint: '' },
    })
    nodes.push(refNode)
  }

  // 主生成节点
  const isVideo = ctx.ctx === 'shot-take'
  const genType: StudioNodeType = isVideo ? 'gen_video' : 'gen_image'
  const genNode = createStudioNode(genType, { x: 480, y: 200 }, {
    label: isVideo ? '视频生成' : '图片生成',
    prompt: { positive: ctx.label, negative: '' },
  })
  nodes.push(genNode)
  edges.push({
    id: `e_${ctxNode.id}_${genNode.id}`,
    source: ctxNode.id,
    sourceHandle: 'out-ctx',
    target: genNode.id,
    targetHandle: 'in-text',
    type: 'studio',
    data: { animated: true },
  })

  // 登记节点
  const registerNode = createStudioNode('register', { x: 820, y: 200 }, {
    label: '登记',
    register: {
      kind:
        ctx.ctx === 'bible-character'
          ? 'character_ref'
          : ctx.ctx === 'bible-location'
            ? 'location_ref'
            : ctx.ctx === 'bible-prop'
              ? 'prop'
              : ctx.ctx === 'bible-costume'
                ? 'misc'
                : ctx.ctx === 'bible-style'
                  ? 'misc'
                  : ctx.ctx === 'shot-take'
                    ? 'take_video'
                    : 'storyboard',
      name: ctx.label,
    },
  })
  nodes.push(registerNode)
  edges.push({
    id: `e_${genNode.id}_${registerNode.id}`,
    source: genNode.id,
    sourceHandle: isVideo ? 'out-video' : 'out-image',
    target: registerNode.id,
    targetHandle: isVideo ? 'in-video' : 'in-image',
    type: 'studio',
    data: { animated: true },
  })

  return { nodes, edges }
}

export function registerKindForCtx(ctx: CanvasCtx): string {
  switch (ctx) {
    case 'bible-character':
      return 'character_ref'
    case 'bible-location':
      return 'location_ref'
    case 'bible-prop':
      return 'prop'
    case 'bible-costume':
      return 'misc'
    case 'bible-style':
      return 'misc'
    case 'shot-frame':
      return 'storyboard'
    case 'shot-take':
      return 'take_video'
    default:
      return 'misc'
  }
}

const GROUP_PAD = 28
const GROUP_LABEL_H = 24

/** 将当前选中的多个节点打组（React Flow parentId） */
export function groupSelectedNodes(nodes: StudioFlowNode[]): StudioFlowNode[] | null {
  const selected = nodes.filter(
    (n) => n.selected && n.type !== 'group' && n.data.nodeType !== 'group' && !n.parentId,
  )
  if (selected.length < 2) return null

  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const n of selected) {
    const { w, h } = estimateNodeSize(n)
    minX = Math.min(minX, n.position.x)
    minY = Math.min(minY, n.position.y)
    maxX = Math.max(maxX, n.position.x + w)
    maxY = Math.max(maxY, n.position.y + h)
  }

  const gx = minX - GROUP_PAD
  const gy = minY - GROUP_PAD - GROUP_LABEL_H
  const gw = maxX - minX + GROUP_PAD * 2
  const gh = maxY - minY + GROUP_PAD * 2 + GROUP_LABEL_H

  const groupNode = createStudioNode('group', { x: gx, y: gy }, {
    label: '分组',
    groupMeta: { title: '分组', color: '#6366f1' },
  })
  groupNode.style = { width: gw, height: gh }

  const childIds = new Set(selected.map((n) => n.id))
  const next = nodes.map((n) => {
    if (!childIds.has(n.id)) return { ...n, selected: false }
    return {
      ...n,
      parentId: groupNode.id,
      position: {
        x: n.position.x - gx,
        y: n.position.y - gy,
      },
      selected: false,
    }
  })

  return [groupNode, ...next]
}

/** 解散分组：子节点回到画布绝对坐标 */
export function ungroupNode(nodes: StudioFlowNode[], groupId: string): StudioFlowNode[] | null {
  const group = nodes.find((n) => n.id === groupId)
  if (!group) return null
  return nodes
    .filter((n) => n.id !== groupId)
    .map((n) => {
      if (n.parentId !== groupId) return n
      return {
        ...n,
        parentId: undefined,
        extent: undefined,
        position: {
          x: group.position.x + n.position.x,
          y: group.position.y + n.position.y,
        },
      }
    })
}
